import { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useShowrooms,
  useCreateShowroom,
  useEditShowroom,
  useDeleteShowroom,
} from "hooks/showroomHook"
import { useListParams } from "hooks/useListParams"
import StatusBadge from "components/Common/StatusBadge"

const ShowroomList = () => {
  document.title = "Showroom List"

  // State
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [showroom, setShowroom] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const {
    params,
    setPage,
    setSearch,
    resetParams,
    hasActiveFilters,
    updateParams,
  } = useListParams({
    page: 1,
    search: "",
  })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: showroomsResponse,
    isLoading,
    refetch: showroomRefetch,
    isRefetching: showroomRefetchLoading,
  } = useShowrooms(queryParams)

  const { mutateAsync: createShowroom, isPending: showroomCreateLoading } =
    useCreateShowroom()
  const { mutateAsync: editShowroom, isPending: showroomEditLoading } =
    useEditShowroom()
  const { mutateAsync: deleteShowroom, isPending: showroomDeleteLoading } =
    useDeleteShowroom()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setShowroom(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setShowroom(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleShowroomClick = useCallback(
    arg => {
      setShowroom(arg)
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(data => {
    setShowroom(data)
    setDeleteModal(true)
  }, [])

  const handleDeleteShowroom = useCallback(async () => {
    if (showroom?.id) {
      try {
        const res = await deleteShowroom(showroom.id)
        toast.success(res?.message ?? "Showroom deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete showroom:", error)
        toast.error("Failed to delete showroom.")
      }
    }
  }, [showroom, deleteShowroom])

  const handleShowroomListRefresh = () => {
    showroomRefetch()
  }

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().trim().min(2).required("Please enter showroom name"),
    address: Yup.string().trim().nullable(),
    email: Yup.string().email("Invalid email format").nullable(),
    mobile: Yup.string().nullable(),
    stock_url: Yup.string().url("Invalid stock URL").nullable(),
  })

  const onSubmit = useCallback(
    async values => {
      const payload = {
        name: values.name,
        address: values.address,
        email: values.email,
        mobile: values.mobile,
        stock_url: values.stock_url || null,
        proprietor_name: values.proprietor_name || null,
        proprietor_mobile: values.proprietor_mobile || null,
        proprietor_address: values.proprietor_address || null,
        short_name: values.short_name || null,
        facebook: values.facebook || null,
        youtube: values.youtube || null,
        instagram: values.instagram || null,
        linkedin: values.linkedin || null,
        tiktok: values.tiktok || null,
        description: values.description || null,
        is_active: values.is_active ?? true,
      }

      const action = isEdit
        ? editShowroom({ id: showroom.id, ...payload })
        : createShowroom(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Showroom ${isEdit ? "updated" : "created"} successfully.`
        )
        toggle()
      } catch (error) {
        console.error("Failed to save showroom:", error)
        toast.error("Failed to save showroom.")
      }
    },
    [isEdit, showroom, createShowroom, editShowroom, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: showroom?.name || "",
      short_name: showroom?.short_name || "",
      proprietor_name: showroom?.proprietor_name || "",
      proprietor_mobile: showroom?.proprietor_mobile || "",
      proprietor_address: showroom?.proprietor_address || "",
      address: showroom?.address || "",
      email: showroom?.email || "",
      mobile: showroom?.mobile || "",
      stock_url: showroom?.stock_url || "",
      facebook: showroom?.facebook || "",
      youtube: showroom?.youtube || "",
      instagram: showroom?.instagram || "",
      linkedin: showroom?.linkedin || "",
      tiktok: showroom?.tiktok || "",
      description: showroom?.description || "",
      is_active: showroom?.is_active ?? true,
    },
    validationSchema,
    onSubmit,
  })

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Short Name",
        accessorKey: "short_name",
      },
      {
        header: "Address",
        accessorKey: "address",
        cell: ({ row }) => {
          return <div className="text-wrap" style={{maxWidth: 200}}>
            {row.original.address}
          </div>
        },
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Mobile",
        accessorKey: "mobile",
      },
      {
        header: "Stock URL",
        accessorKey: "stock_url",
        cell: ({ row }) =>
          row.original.stock_url ? (
            <a href={row.original.stock_url} target="_blank" rel="noreferrer">
              View Stock
            </a>
          ) : (
            "-"
          ),
      },
      {
        header: "Status",
        accessorKey: "is_active",
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.is_active}
            trueLabel="Active"
            falseLabel="Inactive"
          />
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Link
                to={`/showroom-list/${row.original.slug ?? ""}/view`}
                className="btn btn-sm btn-soft-primary"
                id={`viewtooltip-${row.original.id}`}
              >
                <i className="mdi mdi-eye-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`viewtooltip-${row.original.id}`}
                >
                  View
                </UncontrolledTooltip>
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleShowroomClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleShowroomClick, onClickDelete]
  )

  const showroomsData = showroomsResponse?.data?.data || []
  const paginationInfo = showroomsResponse?.data

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteShowroom}
        loading={showroomDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Showroom" breadcrumbItem="Showroom Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 card-title">Showroom Lists</h5>
                  <div>
                    <Button color="primary" onClick={handleAddNewClick}>
                      Add New Showroom
                    </Button>

                    <Button
                      color="light"
                      onClick={handleShowroomListRefresh}
                      className="ms-1"
                      id="refreshTooltip"
                    >
                      <i
                        className={`mdi mdi-refresh ${
                          showroomRefetchLoading ? "mdi-spin" : ""
                        }`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target="refreshTooltip"
                      >
                        Refresh
                      </UncontrolledTooltip>
                    </Button>

                    {hasActiveFilters && (
                      <Button color="danger" onClick={resetParams}>
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={showroomsData}
                    isPagination
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || showroomRefetchLoading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Showroom" : "Add Showroom"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <Label>Name</Label>
                <Input
                  name="name"
                  value={validation.values.name}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.name}
                />
                <FormFeedback>{validation.errors.name}</FormFeedback>

                <Label>Short Name</Label>
                <Input
                  name="short_name"
                  value={validation.values.short_name}
                  onChange={validation.handleChange}
                />

                <Label>Address</Label>
                <Input
                  name="address"
                  type="textarea"
                  value={validation.values.address}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.address}
                />
                <FormFeedback>{validation.errors.address}</FormFeedback>

                <Label>Proprietor Name</Label>
                <Input
                  name="proprietor_name"
                  value={validation.values.proprietor_name}
                  onChange={validation.handleChange}
                />

                <Label>Proprietor Mobile</Label>
                <Input
                  name="proprietor_mobile"
                  value={validation.values.proprietor_mobile}
                  onChange={validation.handleChange}
                />

                <Label>Proprietor Address</Label>
                <Input
                  name="proprietor_address"
                  type="textarea"
                  value={validation.values.proprietor_address}
                  onChange={validation.handleChange}
                />

                <Label>Email</Label>
                <Input
                  name="email"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                />
                <Label>Mobile</Label>
                <Input
                  name="mobile"
                  value={validation.values.mobile}
                  onChange={validation.handleChange}
                />

                <Label>Stock URL</Label>
                <Input
                  name="stock_url"
                  value={validation.values.stock_url}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.stock_url}
                />
                <FormFeedback>{validation.errors.stock_url}</FormFeedback>

                <div className="text-end mt-3">
                  <Button
                    type="submit"
                    color="success"
                    disabled={showroomCreateLoading || showroomEditLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Showroom"}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </>
  )
}

export default ShowroomList
